import { useEffect, useState } from 'react';
import { Row, Col } from 'antd';
import { Navigate, useParams } from 'react-router-dom';
import { StyledPageWrapper } from 'components/new-ui/base/wrappers/styles';
import FormContent from 'components/new-ui/composed/multistep-form/FormContent';
import { MultiStepFormProvider } from 'components/new-ui/composed/multistep-form/MultiStepFormProvider';
import useBreadcrumb from 'hooks/useBreadcrumb';
import { StepsSidebar } from 'components/new-ui/composed/multistep-form/StepsSidebar';
import { createManifestJobInitialValues } from './types';
import { getStepsConfig } from './stepsConfig';
import CreateManifestJobHeader from './CreateManifestJobHeader';
import useManifests from 'hooks/useManifests';
import { checkCanAssignJobs } from 'config/roles/roles';
import useSession from 'hooks/useSession';

const CreateManifestJob = () => {
    const { setBreadcrumbRoutes } = useBreadcrumb();
    const { manifestId } = useParams<{ manifestId: string }>();
    const { manifest, getManifestById, isLoading } = useManifests();
    const { userInfo } = useSession();

    const [notFound, setNotFound] = useState(false);
    const [requested, setRequested] = useState(false);

    const canAssignJobs = checkCanAssignJobs(userInfo);

    useEffect(() => {
        setBreadcrumbRoutes([
            { breadcrumbName: 'Home', path: '/home' },
            { breadcrumbName: 'Manifests', path: '../manifests' },
            { breadcrumbName: manifest?.referenceId ?? '-', path: `../manifests/${manifestId}` },
            { breadcrumbName: 'Create Job', path: '' },
        ]);
    }, [manifestId, setBreadcrumbRoutes, manifest?.referenceId]);

    useEffect(() => {
        if (!manifestId) return;

        setNotFound(false);

        if (manifest?.id === manifestId) {
            setRequested(true);
            return;
        }

        setRequested(true);
        getManifestById(manifestId).catch((err: any) => {
            const status = err?.response?.status ?? err?.status;
            if (status === 404) setNotFound(true);
        });
    }, [manifestId, manifest?.id, getManifestById]);

    const hasMatch = manifest?.id === manifestId;

    if (!requested || isLoading) return null;

    if (notFound || !hasMatch) return <Navigate to="/manifests" replace />;

    return (
        <MultiStepFormProvider initialValues={createManifestJobInitialValues} steps={getStepsConfig({ canAssignJobs })}>
            <StyledPageWrapper>
                <CreateManifestJobHeader />
                <Row gutter={20} style={{ marginTop: 32 }}>
                    <Col style={{ maxWidth: 210 }} sm={24} md={6}>
                        <StepsSidebar />
                    </Col>
                    <Col sm={24} md={18}>
                        <FormContent />
                    </Col>
                </Row>
            </StyledPageWrapper>
        </MultiStepFormProvider>
    );
};

export default CreateManifestJob;
